import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { API_DOCS } from './ListiniFornitatoriPage.api';

// ── Interfacce ────────────────────────────────────────────────────────────────

type StrategiaAggiornamento = 'SOLO_PREZZO' | 'PREZZO_E_ANAGRAFICA';

interface ImportListino {
  id: number;
  societaId: number;
  etichetta: string;
  note: string | null;
  stato: 'IN_CORSO' | 'COMPLETATO' | 'COMPLETATO_CON_ERRORI' | 'ERRORE';
  importatoAt: string;
  totaleRighe: number;
  righeCreate: number;
  righeAggiornate: number;
  righeErrore: number;
  gcsPathExcel: string | null;
  gcsPathLog: string | null;
  strategiaAggiornamento?: StrategiaAggiornamento;
  fornitore: { id: number; ragioneSociale: string | null; nome: string | null };
  utente: { id: number; nome: string; email: string };
}

interface UploadRisposta {
  id: number; // solo l'id — l'import parte in background
}

interface Fornitore {
  id: number;
  ragioneSociale: string | null;
  nome: string | null;
}

interface ValoriAmmessi {
  tipologie: { valore: string; label: string }[];
  unitaMisura: { id: number; simbolo: string; nome: string }[];
  marche: { id: number; nome: string }[];
  collezioni: { id: number; nome: string; marcaId: number }[];
  formati: { id: number; nome: string }[];
  categorieMerceologiche: { id: number; nome: string }[];
  categorieConfigurazione: { id: number; nome: string }[];
  effetti: { id: number; nome: string }[];
}

const LIMIT_STORICO = 20;

// ── Helper badge ──────────────────────────────────────────────────────────────

const badgeStato = (stato: string) => {
  const map: Record<string, string> = {
    COMPLETATO:             'bop-badge-success',
    COMPLETATO_CON_ERRORI:  'bop-badge-warning',
    ERRORE:                 'bop-badge-danger',
    IN_CORSO:               'bop-badge-neutral',
  };
  const labels: Record<string, string> = {
    COMPLETATO:            'Completato',
    COMPLETATO_CON_ERRORI: 'Con errori',
    ERRORE:                'Errore',
    IN_CORSO:              'In corso',
  };
  return (
    <span className={`bop-badge ${map[stato] || 'bop-badge-neutral'} ${
      stato === 'IN_CORSO' ? 'listini-badge-in-corso' : ''
    }`}>
      {labels[stato] || stato}
    </span>
  );
};

// ── Sotto-componenti Guida valori ammessi ─────────────────────────────────────

function GuidaChip({ valore, descrizione }: { valore: string; descrizione?: string }) {
  const [copiato, setCopiato] = React.useState(false);
  const handleClick = () => {
    navigator.clipboard.writeText(valore).catch(() => {});
    setCopiato(true);
    setTimeout(() => setCopiato(false), 1400);
  };
  return (
    <button
      className={`listini-guida-chip${copiato ? ' listini-guida-chip--copiato' : ''}`}
      onClick={handleClick}
      title={descrizione || valore}
    >
      {copiato ? '✓ copiato' : valore}
    </button>
  );
}

interface GuidaFieldCardProps {
  nome: string;
  colonnaExcel: string;
  tipo: 'ENUM' | 'TABELLATO';
  valori: string[];
  descrizioni?: Record<string, string>;
  cascata?: boolean;
}

const GUIDA_SHOW_LIMIT = 10;

function GuidaFieldCard({ nome, colonnaExcel, tipo, valori, descrizioni, cascata }: GuidaFieldCardProps) {
  const [espansa, setEspansa] = React.useState(false);
  const [cerca, setCerca] = React.useState('');

  const valoriFiltrati = cerca
    ? valori.filter(v => v.toLowerCase().includes(cerca.toLowerCase()))
    : valori;

  const visibili = espansa ? valoriFiltrati : valoriFiltrati.slice(0, GUIDA_SHOW_LIMIT);
  const nascosti = valoriFiltrati.length - GUIDA_SHOW_LIMIT;

  return (
    <div className={`listini-guida-field-card${cascata ? ' listini-guida-field-card--cascata' : ''}`}>
      <div className="listini-guida-field-header">
        <div className="listini-guida-field-header-left">
          <span className="listini-guida-field-nome">{nome}</span>
          <code className="listini-guida-field-col">{colonnaExcel}</code>
        </div>
        <span className={`bop-badge ${tipo === 'ENUM' ? 'bop-badge-info' : 'bop-badge-warning'}`}>
          {tipo}
        </span>
      </div>
      <div className="listini-guida-field-body">
        {valori.length > GUIDA_SHOW_LIMIT && (
          <input
            type="text"
            className="bop-input listini-guida-search"
            placeholder="Cerca..."
            value={cerca}
            onChange={e => { setCerca(e.target.value); setEspansa(false); }}
          />
        )}
        <div className="listini-guida-chip-list">
          {visibili.map(v => (
            <GuidaChip key={v} valore={v} descrizione={descrizioni?.[v]} />
          ))}
        </div>
        {!espansa && nascosti > 0 && (
          <button className="listini-guida-show-more" onClick={() => setEspansa(true)}>
            Mostra altri {nascosti} ▼
          </button>
        )}
        {espansa && valoriFiltrati.length > GUIDA_SHOW_LIMIT && (
          <button className="listini-guida-show-more" onClick={() => setEspansa(false)}>
            Mostra meno ▲
          </button>
        )}
        <div className="listini-guida-copy-hint">Clicca per copiare</div>
      </div>
    </div>
  );
}

interface GuidaFieldCardCollezioneProps {
  collezioni: { id: number; nome: string; marcaId: number }[];
  marche: { id: number; nome: string }[];
  marcaFiltro: number | null;
  onMarcaFiltroChange: (id: number | null) => void;
}

function GuidaFieldCardCollezione({ collezioni, marche, marcaFiltro, onMarcaFiltroChange }: GuidaFieldCardCollezioneProps) {
  const [espansa, setEspansa] = React.useState(false);
  const [cerca, setCerca] = React.useState('');

  const collFiltered = collezioni
    .filter(c => !marcaFiltro || c.marcaId === marcaFiltro)
    .filter(c => !cerca || c.nome.toLowerCase().includes(cerca.toLowerCase()));

  const visibili = espansa ? collFiltered : collFiltered.slice(0, GUIDA_SHOW_LIMIT);
  const nascosti = collFiltered.length - GUIDA_SHOW_LIMIT;

  return (
    <div className="listini-guida-field-card listini-guida-field-card--cascata">
      <div className="listini-guida-field-header">
        <div className="listini-guida-field-header-left">
          <span className="listini-guida-field-nome">Collezione</span>
          <code className="listini-guida-field-col">collezione</code>
          <span className="listini-guida-field-dep">dipende da Marca</span>
        </div>
        <div className="bop-flex">
          <span className="bop-badge bop-badge-neutral">{collFiltered.length} valori</span>
          <span className="bop-badge bop-badge-warning">TABELLATO</span>
        </div>
      </div>
      <div className="listini-guida-field-body">
        <div className="listini-guida-marca-row">
          <label className="bop-label">Filtra per marca:</label>
          <select
            className="bop-select listini-guida-marca-select"
            value={marcaFiltro ?? ''}
            onChange={e => {
              onMarcaFiltroChange(e.target.value ? parseInt(e.target.value, 10) : null);
              setEspansa(false);
            }}
          >
            <option value="">— tutte le marche —</option>
            {marche.map(m => (
              <option key={m.id} value={m.id}>{m.nome}</option>
            ))}
          </select>
        </div>
        <input
          type="text"
          className="bop-input listini-guida-search"
          placeholder="Cerca collezione..."
          value={cerca}
          onChange={e => { setCerca(e.target.value); setEspansa(false); }}
        />
        <div className="listini-guida-chip-list">
          {visibili.map(c => (
            <GuidaChip key={c.id} valore={c.nome} />
          ))}
          {collFiltered.length === 0 && (
            <span className="listini-guida-empty">
              {marcaFiltro ? 'Nessuna collezione per questa marca' : 'Nessuna collezione disponibile'}
            </span>
          )}
        </div>
        {!espansa && nascosti > 0 && (
          <button className="listini-guida-show-more" onClick={() => setEspansa(true)}>
            Mostra altri {nascosti} ▼
          </button>
        )}
        {espansa && collFiltered.length > GUIDA_SHOW_LIMIT && (
          <button className="listini-guida-show-more" onClick={() => setEspansa(false)}>
            Mostra meno ▲
          </button>
        )}
        <div className="listini-guida-copy-hint">Clicca per copiare</div>
      </div>
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function ListiniFornitatoriPage() {
  const navigate = useNavigate();
  const { societaId: filterSocietaId, pronta } = useFiltroSocieta();
  const { utente } = usePermesso();

  // ── Stato form import ───────────────────────────────────────────────────────
  const [fornitoreId, setFornitoreId] = useState('');
  const [cercaForn,   setCercaForn]   = useState('');
  const [etichetta,   setEtichetta]   = useState('');
  const [note,        setNote]        = useState('');
  const [file,        setFile]        = useState<File | null>(null);
  const [importing,   setImporting]   = useState(false);
  const [fornitori,   setFornitori]   = useState<Fornitore[]>([]);
  const [oraCorrente, setOraCorrente] = useState('');
  const [strategiaAggiornamento, setStrategiaAggiornamento] = useState<StrategiaAggiornamento>('SOLO_PREZZO');
  const [modaleConfermaAperta, setModaleConfermaAperta] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  // ── Toast ───────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  // ── Stato guida valori ammessi ──────────────────────────────────────────────
  const [valoriAmmessi,   setValoriAmmessi]   = useState<ValoriAmmessi | null>(null);
  const [loadingValori,   setLoadingValori]   = useState(false);
  const [erroreValori,    setErroreValori]    = useState(false);
  const [guidaAperta,     setGuidaAperta]     = useState(false);
  const [marcaFiltro,     setMarcaFiltro]     = useState<number | null>(null);
  const valoriCaricatiRef = useRef<boolean>(false);

  // ── Stato storico ───────────────────────────────────────────────────────────
  const [storico,       setStorico]       = useState<ImportListino[]>([]);
  const [totaleStorico, setTotaleStorico] = useState(0);
  const [paginaStorico, setPaginaStorico] = useState(1);
  const [loadingStorico, setLoadingStorico] = useState(false);
  const [utenti,  setUtenti]  = useState<{ id: number; nome: string }[]>([]);
  const [filtri,  setFiltri]  = useState({ fornitoreId: '', utenteId: '', dal: '', al: '' });

  // ── Orologio ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const aggiorna = () => setOraCorrente(new Date().toLocaleString('it-IT'));
    aggiorna();
    const timer = setInterval(aggiorna, 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Carica valori ammessi (lazy — solo quando la guida viene aperta) ─────────
  const caricaValoriAmmessi = useCallback(async () => {
    if (!pronta || !filterSocietaId) return;
    if (valoriCaricatiRef.current) return;
    valoriCaricatiRef.current = true;
    setLoadingValori(true);
    setErroreValori(false);
    try {
      // @api GET /api/anagrafiche/articoli/valori-ammessi?societaId=N -> {"success":true,"data":"ValoriAmmessi"}
      const res = await apiFetch<{ success: boolean; data: ValoriAmmessi }>(
        `/api/anagrafiche/articoli/valori-ammessi?societaId=${filterSocietaId}`
      );
      setValoriAmmessi(res.data);
    } catch (err) {
      console.error('[ListiniPage] caricaValoriAmmessi', err);
      valoriCaricatiRef.current = false; // permetti retry
      setErroreValori(true);
    } finally {
      setLoadingValori(false);
    }
  }, [pronta, filterSocietaId]); // eslint-disable-line

  useEffect(() => {
    if (guidaAperta) caricaValoriAmmessi();
  }, [guidaAperta, caricaValoriAmmessi]);

  // Reset cache quando cambia la società
  useEffect(() => {
    valoriCaricatiRef.current = false;
    setValoriAmmessi(null);
    setErroreValori(false);
  }, [filterSocietaId]);

  // ── Carica fornitori e utenti ────────────────────────────────────────────────
  useEffect(() => {
    if (!pronta) return;
    apiFetch<{ data: Fornitore[] }>(
      // @api GET /api/nominativi?ruolo=FORNITORE&limit=200&societaId=N -> {"data":"Nominativo[]","total":0}
      `/api/nominativi?ruolo=FORNITORE&limit=200${filterSocietaId ? `&societaId=${filterSocietaId}` : ''}`
    ).then(r => setFornitori(r.data ?? [])).catch(console.error);

    apiFetch<{ success: boolean; data: { id: number; nome: string }[] }>(
      // @api GET /api/anagrafiche/listini/utenti?societaId=N -> {"success":true,"data":[{"id":"number","nome":"string"}]}
      `/api/anagrafiche/listini/utenti${filterSocietaId ? `?societaId=${filterSocietaId}` : ''}`
    ).then(r => setUtenti(r.data ?? [])).catch(console.error);
  }, [pronta, filterSocietaId]); // eslint-disable-line

  // ── Carica storico ───────────────────────────────────────────────────────────
  const caricaStorico = useCallback(async (p: number) => {
    if (!pronta) return;
    setLoadingStorico(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT_STORICO) });
      if (filterSocietaId)     params.set('societaId',   String(filterSocietaId));
      if (filtri.fornitoreId)  params.set('fornitoreId', filtri.fornitoreId);
      if (filtri.utenteId)     params.set('utenteId',    filtri.utenteId);
      if (filtri.dal)          params.set('dal', filtri.dal);
      if (filtri.al)           params.set('al',  filtri.al);
      // @api GET /api/anagrafiche/listini -> {"success":true,"data":"ImportListino[]","total":"number","page":"number","limit":"number"}
      const res = await apiFetch<{ success: boolean; data: ImportListino[]; total: number; page: number }>(
        `/api/anagrafiche/listini?${params}`
      );
      setStorico(res.data);
      setTotaleStorico(res.total);
      setPaginaStorico(p);
    } catch (err) {
      console.error('[ListiniPage] caricaStorico', err);
    } finally {
      setLoadingStorico(false);
    }
  }, [pronta, filterSocietaId, filtri]); // eslint-disable-line

  useEffect(() => {
    caricaStorico(1);
  }, [pronta, filterSocietaId]); // eslint-disable-line

  // ── Polling automatico per record IN_CORSO ────────────────────────────────
  useEffect(() => {
    const hasInCorso = storico.some(imp => imp.stato === 'IN_CORSO');
    if (!hasInCorso) return;
    const interval = setInterval(() => caricaStorico(paginaStorico), 3000);
    return () => clearInterval(interval);
  }, [storico, paginaStorico, caricaStorico]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleScaricaTemplate = async () => {
    try {
      // @api GET /api/anagrafiche/listini/template -> file xlsx
      await apiDownload(`/api/anagrafiche/listini/template?_t=${Date.now()}`, 'template_listino_bop.xlsx');
    } catch (err: any) {
      showToast('Errore download template: ' + err.message, 'error');
    }
  };

  const handleImport = async () => {
    setModaleConfermaAperta(false);
    if (!fornitoreId || !etichetta.trim() || !file) return;
    if (!filterSocietaId) {
      showToast('Seleziona prima una società dalla barra in alto', 'error');
      return;
    }
    setImporting(true);
    setToast(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('societaId', String(filterSocietaId));
      formData.append('fornitoreId', fornitoreId);
      formData.append('etichetta', etichetta.trim());
      formData.append('note', note.trim());
      formData.append('strategiaAggiornamento', strategiaAggiornamento);
      // @api POST /api/anagrafiche/listini/upload (multipart: file, societaId, fornitoreId, etichetta, note, strategiaAggiornamento) -> {"success":true,"data":{"id":"number"}}
      const res = await apiFetch<{ success: boolean; data: UploadRisposta }>(
        '/api/anagrafiche/listini/upload',
        { method: 'POST', body: formData }
      );
      // Reset form
      setFile(null);
      setEtichetta('');
      setNote('');
      setFornitoreId('');
      setCercaForn('');
      setStrategiaAggiornamento('SOLO_PREZZO');
      if (inputFileRef.current) inputFileRef.current.value = '';
      // Ricarica la tabella — il record IN_CORSO è già presente
      await caricaStorico(1);
      showToast(`Import avviato (id #${res.data.id}) — il record apparirà in tabella. Clicca "Dettagli" per seguire l'avanzamento.`);
    } catch (err: any) {
      showToast(err.message || 'Errore durante l\'upload', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleScaricaLog = async (id: number) => {
    try {
      // L'endpoint restituisce il file, non un JSON con un link: chiamarlo con
      // apiFetch faceva fallire il parsing e il download non partiva mai.
      // @api GET /api/anagrafiche/listini/:id/file-log -> file stream (attachment)
      await apiDownload(`/api/anagrafiche/listini/${id}/file-log`, `log_import_${id}.txt`);
    } catch (err: any) {
      showToast('Errore download log: ' + err.message, 'error');
    }
  };

  // ── Fornitori filtrati per autocomplete ──────────────────────────────────────
  const fornitoriFiltrati = fornitori.filter(f => {
    const n = f.ragioneSociale || f.nome || '';
    return n.toLowerCase().includes(cercaForn.toLowerCase());
  });

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout>
      {/* Toast */}
      {toast && (
        <div className={`amministrazione-toast ${
          toast.type === 'success' ? 'amministrazione-toast-success' : 'amministrazione-toast-error'
        }`}>
          {toast.msg}
        </div>
      )}

      <div className="listini-page">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Import Listini</h1>
        </div>

        {/* SEZIONE 1 — Pannello Import */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Importa Listino Fornitore</h2>
          </div>
          <div className="bop-card__body">
          {/* Avviso se nessuna società selezionata */}
          {!filterSocietaId && (
            <div className="listini-avviso-societa">
              Seleziona una società dalla barra in alto per importare un listino.
            </div>
          )}

          <div className="bop-form-row">
            {/* Colonna 1 */}
            <div className="listini-form-col">
              {/* Autocomplete fornitore */}
              <div className="bop-form-group">
                <label className="bop-label">Fornitore *</label>
                <div className="listini-forn-wrapper">
                  <input
                    type="text"
                    className="bop-input"
                    placeholder="Cerca fornitore..."
                    value={cercaForn}
                    onChange={e => {
                      setCercaForn(e.target.value);
                      if (fornitoreId) setFornitoreId('');
                    }}
                  />
                  {cercaForn && fornitoriFiltrati.length > 0 && !fornitoreId && (
                    <div className="listini-forn-dropdown">
                      {fornitoriFiltrati.slice(0, 10).map(f => (
                        <div
                          key={f.id}
                          className="listini-forn-dropdown-item"
                          onClick={() => {
                            setFornitoreId(String(f.id));
                            setCercaForn(f.ragioneSociale || f.nome || '');
                          }}
                        >
                          {f.ragioneSociale || f.nome}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {fornitoreId && (
                  <div className="listini-forn-selezionato">
                    <span>
                      ✓ {fornitori.find(f => String(f.id) === fornitoreId)?.ragioneSociale ||
                          fornitori.find(f => String(f.id) === fornitoreId)?.nome}
                    </span>
                    <button
                      type="button"
                      className="listini-forn-btn-deseleziona"
                      onClick={() => { setFornitoreId(''); setCercaForn(''); }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Etichetta * (max 30 caratteri)</label>
                <input
                  type="text"
                  className="bop-input"
                  maxLength={30}
                  value={etichetta}
                  onChange={(e) => setEtichetta(e.target.value)}
                  placeholder="Es. Listino primavera 2026"
                />
              </div>
            </div>

            {/* Colonna 2 */}
            <div className="listini-form-col">
              <div className="bop-form-group">
                <label className="bop-label">Utente</label>
                <input
                  type="text"
                  className="bop-input"
                  value={utente?.nome || ''}
                  readOnly
                  disabled
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data/Ora</label>
                <input
                  type="text"
                  className="bop-input"
                  value={oraCorrente}
                  readOnly
                  disabled
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Note (max 100 caratteri)</label>
                <input
                  type="text"
                  className="bop-input"
                  maxLength={100}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Opzionale"
                />
              </div>
            </div>
          </div>

          {/* Strategia aggiornamento articoli già in archivio */}
          <div className="bop-form-group listini-strategia-group">
            <label className="bop-label">Gestione articoli già in archivio *</label>
            <div className="listini-strategia-radio-list">
              <label className="listini-strategia-radio">
                <input
                  type="radio"
                  name="strategiaAggiornamento"
                  value="SOLO_PREZZO"
                  checked={strategiaAggiornamento === 'SOLO_PREZZO'}
                  onChange={() => setStrategiaAggiornamento('SOLO_PREZZO')}
                />
                <span className="listini-strategia-radio-titolo">Sovrascrivi solo il prezzo</span>
                <span className="listini-strategia-radio-desc">
                  Degli articoli già presenti in archivio verrà aggiornato solo il prezzo di vendita.
                  Descrizione, marca, collezione, categorie ecc. restano invariati.
                </span>
              </label>
              <label className="listini-strategia-radio">
                <input
                  type="radio"
                  name="strategiaAggiornamento"
                  value="PREZZO_E_ANAGRAFICA"
                  checked={strategiaAggiornamento === 'PREZZO_E_ANAGRAFICA'}
                  onChange={() => setStrategiaAggiornamento('PREZZO_E_ANAGRAFICA')}
                />
                <span className="listini-strategia-radio-titolo">Sovrascrivi prezzo, descrizione ed altri valori</span>
                <span className="listini-strategia-radio-desc">
                  Vengono sovrascritti anche descrizione, UM, marca, collezione, formato,
                  categorie, effetto, quantità min/pallet. La tipologia dell'articolo non viene
                  mai modificata. I campi non presenti nel listino (flag e-commerce, note, ecc.)
                  restano invariati.
                </span>
              </label>
            </div>
          </div>

          {/* Area azioni */}
          <div className="listini-azioni-form">
            <button className="bop-btn-secondary" onClick={handleScaricaTemplate}>
              ⬇ Scarica Template
            </button>
            <div
              className={`listini-upload-area ${file ? 'has-file' : ''}`}
              onClick={() => inputFileRef.current?.click()}
            >
              {file ? `📎 ${file.name}` : 'Clicca o trascina qui il file Excel (.xlsx)'}
              <input
                ref={inputFileRef}
                type="file"
                accept=".xlsx,.xls"
                className="listini-input-file-hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            <button
              className="bop-btn-primary"
              onClick={() => setModaleConfermaAperta(true)}
              disabled={!fornitoreId || !etichetta.trim() || !file || importing || !filterSocietaId}
            >
              {importing ? '⏳ Upload in corso...' : '▶ Avvia Import'}
            </button>
          </div>
          </div>
        </div>

        {/* SEZIONE 2 — Guida valori ammessi */}
        <div className="bop-card">
          <div
            className="bop-card__header listini-guida-header"
            onClick={() => setGuidaAperta(v => !v)}
          >
            <h2 className="bop-card__title">
              Guida valori ammessi
            </h2>
            <div className="listini-guida-header-right">
              <span className="bop-badge bop-badge-warning">8 campi critici</span>
              <span className="listini-guida-chevron">{guidaAperta ? '▾' : '▸'}</span>
            </div>
          </div>

          {guidaAperta && (
            <div className="bop-card__body">
              {!filterSocietaId && (
                <div className="bop-empty-state">Seleziona una società per vedere i valori disponibili</div>
              )}
              {filterSocietaId && loadingValori && (
                <div className="bop-empty-state">Caricamento valori...</div>
              )}
              {filterSocietaId && !loadingValori && erroreValori && (
                <div className="bop-empty-state">
                  Impossibile caricare i valori. Assicurarsi che il backend sia avviato,
                  poi&nbsp;
                  <button
                    className="listini-guida-show-more"
                    onClick={() => { valoriCaricatiRef.current = false; caricaValoriAmmessi(); }}
                  >
                    riprova
                  </button>
                  .
                </div>
              )}
              {filterSocietaId && !loadingValori && valoriAmmessi && (
                <>
                  <p className="listini-guida-intro">
                    Clicca su un valore per copiarlo negli appunti.
                    Inserisci nell'Excel il valore <strong>esattamente come mostrato</strong>.
                  </p>
                  <div className="listini-guida-grid">

                    <GuidaFieldCard
                      nome="Tipologia"
                      colonnaExcel="tipologia"
                      tipo="ENUM"
                      valori={valoriAmmessi.tipologie.map(t => t.valore)}
                      descrizioni={Object.fromEntries(valoriAmmessi.tipologie.map(t => [t.valore, t.label]))}
                    />

                    <GuidaFieldCard
                      nome="UM Acquisto"
                      colonnaExcel="um_acquisto"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.unitaMisura.map(u => u.simbolo)}
                      descrizioni={Object.fromEntries(valoriAmmessi.unitaMisura.map(u => [u.simbolo, u.nome]))}
                    />

                    <GuidaFieldCard
                      nome="UM Vendita"
                      colonnaExcel="um_vendita"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.unitaMisura.map(u => u.simbolo)}
                      descrizioni={Object.fromEntries(valoriAmmessi.unitaMisura.map(u => [u.simbolo, u.nome]))}
                    />

                    <GuidaFieldCard
                      nome="Marca"
                      colonnaExcel="marca"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.marche.map(m => m.nome)}
                      cascata
                    />

                    <GuidaFieldCardCollezione
                      collezioni={valoriAmmessi.collezioni}
                      marche={valoriAmmessi.marche}
                      marcaFiltro={marcaFiltro}
                      onMarcaFiltroChange={setMarcaFiltro}
                    />

                    <GuidaFieldCard
                      nome="Formato"
                      colonnaExcel="formato"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.formati.map(f => f.nome)}
                    />

                    <GuidaFieldCard
                      nome="Categ. merceologica"
                      colonnaExcel="categ_merceologica"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.categorieMerceologiche.map(c => c.nome)}
                    />

                    <GuidaFieldCard
                      nome="Categ. configurazione"
                      colonnaExcel="categ_configurazione"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.categorieConfigurazione.map(c => c.nome)}
                    />

                    <GuidaFieldCard
                      nome="Effetto"
                      colonnaExcel="effetto"
                      tipo="TABELLATO"
                      valori={valoriAmmessi.effetti.map(e => e.nome)}
                    />

                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* SEZIONE 3 — Storico */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Storico Importazioni</h2>
          </div>
          <div className="bop-card__body">
          {/* Filtri */}
          <div className="listini-storico-filtri">
            <div className="bop-form-group">
              <label className="bop-label">Fornitore</label>
              <select
                className="bop-select"
                value={filtri.fornitoreId}
                onChange={(e) => setFiltri({ ...filtri, fornitoreId: e.target.value })}
              >
                <option value="">Tutti</option>
                {fornitori.map(f => (
                  <option key={f.id} value={f.id}>{f.ragioneSociale || f.nome}</option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Utente</label>
              <select
                className="bop-select"
                value={filtri.utenteId}
                onChange={(e) => setFiltri({ ...filtri, utenteId: e.target.value })}
              >
                <option value="">Tutti</option>
                {utenti.map(u => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Dal</label>
              <input
                type="date"
                className="bop-input"
                value={filtri.dal}
                onChange={(e) => setFiltri({ ...filtri, dal: e.target.value })}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Al</label>
              <input
                type="date"
                className="bop-input"
                value={filtri.al}
                onChange={(e) => setFiltri({ ...filtri, al: e.target.value })}
              />
            </div>
            <div className="bop-form-group listini-filtri-azioni">
              <button
                className="bop-btn-secondary"
                onClick={() => {
                  setFiltri({ fornitoreId: '', utenteId: '', dal: '', al: '' });
                  caricaStorico(1);
                }}
              >
                Reset
              </button>
              <button className="bop-btn-primary" onClick={() => caricaStorico(1)}>
                Cerca
              </button>
            </div>
          </div>
          </div>

          {/* Tabella */}
          {loadingStorico ? (
            <div className="bop-empty-state">Caricamento...</div>
          ) : storico.length === 0 ? (
            <div className="bop-empty-state">Nessuna importazione trovata</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Data/Ora</th>
                  <th>Etichetta</th>
                  <th>Fornitore</th>
                  <th>Utente</th>
                  <th>Note</th>
                  <th>Tot</th>
                  <th>Cre</th>
                  <th>Agg</th>
                  <th>Err</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {storico.map(imp => (
                  <tr key={imp.id}>
                    <td>{new Date(imp.importatoAt).toLocaleString('it-IT')}</td>
                    <td>{imp.etichetta}</td>
                    <td>{imp.fornitore?.ragioneSociale || imp.fornitore?.nome || '-'}</td>
                    <td>{imp.utente?.nome || '-'}</td>
                    <td className="bop-text-muted">{imp.note || '-'}</td>
                    <td className="bop-text-right">{imp.totaleRighe}</td>
                    <td className="bop-text-right">{imp.righeCreate}</td>
                    <td className="bop-text-right">{imp.righeAggiornate}</td>
                    <td className="bop-text-right">{imp.righeErrore}</td>
                    <td>{badgeStato(imp.stato)}</td>
                    <td>
                      <button
                        className="bop-btn-secondary amministrazione-btn-small"
                        onClick={() => navigate(`/anagrafiche/articoli/listini/${imp.id}`)}
                      >
                        Dettagli
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginazione */}
          {totaleStorico > LIMIT_STORICO && (
            <div className="listini-paginazione">
              <span className="bop-text-muted">
                {(paginaStorico - 1) * LIMIT_STORICO + 1}–
                {Math.min(paginaStorico * LIMIT_STORICO, totaleStorico)} di {totaleStorico}
              </span>
              <button
                className="bop-btn-secondary"
                disabled={paginaStorico <= 1}
                onClick={() => caricaStorico(paginaStorico - 1)}
              >
                ← Prec
              </button>
              <button
                className="bop-btn-secondary"
                disabled={paginaStorico * LIMIT_STORICO >= totaleStorico}
                onClick={() => caricaStorico(paginaStorico + 1)}
              >
                Succ →
              </button>
            </div>
          )}
        </div>

        <ApiInspector docs={API_DOCS} />
      </div>

      {/* Modale conferma avvio import */}
      {modaleConfermaAperta && (
        <div className="listini-modale-overlay">
          <div className="listini-modale">
            <div className="listini-modale-header">
              <h2 className="listini-modale-titolo">Conferma avvio import</h2>
            </div>
            <div className="listini-modale-body">
              <div className="listini-modale-riepilogo">
                <div className="listini-modale-riga">
                  <span className="listini-modale-label">Fornitore:</span>
                  <span className="listini-modale-valore">
                    {fornitori.find(f => String(f.id) === fornitoreId)?.ragioneSociale ||
                     fornitori.find(f => String(f.id) === fornitoreId)?.nome || '—'}
                  </span>
                </div>
                <div className="listini-modale-riga">
                  <span className="listini-modale-label">Etichetta:</span>
                  <span className="listini-modale-valore">{etichetta || '—'}</span>
                </div>
                <div className="listini-modale-riga">
                  <span className="listini-modale-label">File:</span>
                  <span className="listini-modale-valore">{file?.name || '—'}</span>
                </div>
                {note && (
                  <div className="listini-modale-riga">
                    <span className="listini-modale-label">Note:</span>
                    <span className="listini-modale-valore">{note}</span>
                  </div>
                )}
              </div>

              <div className="listini-modale-strategia-box">
                <div className="listini-modale-strategia-titolo">
                  Strategia per articoli già in archivio
                </div>
                {strategiaAggiornamento === 'SOLO_PREZZO' ? (
                  <div className="listini-modale-strategia-contenuto">
                    <strong>Sovrascrivi solo il prezzo</strong>
                    <p>
                      Per gli articoli già presenti verrà aggiornato solo il <em>prezzo di
                      vendita</em>. Descrizione, marca, collezione, categorie e tutti gli altri
                      dati anagrafici resteranno invariati.
                    </p>
                  </div>
                ) : (
                  <div className="listini-modale-strategia-contenuto">
                    <strong>Sovrascrivi prezzo, descrizione ed altri valori</strong>
                    <p>
                      Per gli articoli già presenti verranno sovrascritti: prezzo, descrizione
                      base ed estesa, UM vendita e acquisto, convertitore, marca, collezione,
                      formato, categoria merceologica, categoria configurazione, effetto,
                      quantità minima ordinabile e quantità per pallet.
                    </p>
                    <p className="listini-modale-strategia-warning">
                      ⚠ La <strong>tipologia</strong> dell'articolo non viene mai modificata.
                      I campi non presenti nel listino (flag e-commerce, cartella immagini,
                      ecc.) restano invariati.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="listini-modale-footer">
              <button
                className="bop-btn-secondary"
                onClick={() => setModaleConfermaAperta(false)}
                disabled={importing}
              >
                Annulla
              </button>
              <button
                className="bop-btn-primary"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? '⏳ Upload in corso...' : 'Conferma e avvia import'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
